// Data hooks of the share-link screens of a space (GEDX-FE-03): the list of links (of one item or
// of the whole space), the creation (one Idempotency-Key per form content, so a double click never
// makes two links) and the revocation. The space's policy comes from the space itself.
import {
  getShareLinksControllerListQueryKey,
  shareLinksControllerList,
  shareLinksControllerRevoke,
  useSpacesControllerGet,
  type CreateShareLinkDto,
  type CreatedShareLinkDtoOutput,
} from '@batiflow/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntime } from '@/app/providers/runtime-provider';
import type { ShareTarget } from './model';

const prefix = (spaceId: string): string => `/api/v1/spaces/${spaceId}/share-links`;

/** The space, for its policy (shared with the rest of the app through the same cache entry). */
export const useSpacePolicy = (spaceId: string) =>
  useSpacesControllerGet(spaceId, { query: { staleTime: 60_000 } });

/** The links of one document or folder, or of the whole space when `target` is null. */
export function useShareLinks(spaceId: string, target: ShareTarget | null, enabled = true) {
  const params =
    target === null
      ? undefined
      : target.type === 'document'
        ? { documentId: target.id }
        : { folderId: target.id };
  return useQuery({
    queryKey: getShareLinksControllerListQueryKey(spaceId, params),
    queryFn: ({ signal }) => shareLinksControllerList(spaceId, params, signal),
    enabled,
  });
}

interface CreateVariables {
  data: CreateShareLinkDto;
  idempotencyKey: string;
}

/** POST of a link. The answer carries the address, the only time the token is shown. */
export function useCreateShareLink(spaceId: string) {
  const { fetcher } = useRuntime();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, idempotencyKey }: CreateVariables) => {
      const response = await fetcher<CreatedShareLinkDtoOutput>({
        method: 'POST',
        url: prefix(spaceId),
        data,
        idempotencyKey,
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [prefix(spaceId)] }),
  });
}

/** Revokes a link: it stops working at once. */
export function useRevokeShareLink(spaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => shareLinksControllerRevoke(spaceId, linkId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [prefix(spaceId)] }),
  });
}
